import { useEffect, useState } from 'react';

/** Les durées les plus courantes, en un clic. */
const PRESETS = [35, 30, 25, 20, 15, 13, 11];

/** Plafond légal hebdomadaire (Code du travail, art. L3121-20). */
const MAX_HOURS = 48;

/**
 * Heures contractuelles hebdomadaires : les durées courantes en boutons, et
 * n'importe quelle autre au clavier.
 *
 * « Quand je crée un salarié tu m'as mis un nombre d'heures pré-rempli mais il
 *   faut que je puisse mettre le nombre d'heures que je veux, parce que là je
 *   veux 24 h et ça ne marche pas. » Seuls les sept boutons existaient : 24 h,
 *   17,5 h ou 28 h étaient impossibles à saisir, alors que le serveur les
 *   accepte depuis toujours.
 */
export function ContractHoursInput({ value, onChange }: { value: number; onChange: (hours: number) => void }) {
  const [text, setText] = useState(String(value));

  // Un clic sur un bouton change la valeur : le champ la reprend. On ne le
  // réécrit pas pendant la frappe (« 24, » ne doit pas redevenir « 24 »).
  useEffect(() => {
    if (parseHours(text) !== value) setText(String(value).replace('.', ','));
  }, [value]);

  const parsed = parseHours(text);
  const invalid = text.trim() !== '' && parsed === null;

  return (
    <div>
      <div className="grid grid-cols-7 gap-1.5">
        {PRESETS.map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => onChange(h)}
            className={`py-2.5 rounded-lg border font-semibold tabular-nums text-[13px] ${
              value === h
                ? 'bg-primary text-white border-primary ring-2 ring-primary/20'
                : 'bg-white border-slate-200 text-slate-700 hover:border-slate-500'
            }`}
          >
            {h}h
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-[12px] text-slate-500">Ou :</span>
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const n = parseHours(e.target.value);
            if (n !== null) onChange(n);
          }}
          inputMode="decimal"
          aria-label="Heures contractuelles hebdomadaires"
          className={`input w-20 text-center tabular-nums ${invalid ? 'border-danger' : ''}`}
        />
        <span className="text-[12px] text-slate-500">heures par semaine</span>
      </div>
      {invalid && (
        <p className="text-[11px] text-danger mt-1">Un nombre d'heures entre 1 et {MAX_HOURS}, par exemple 24 ou 17,5.</p>
      )}
    </div>
  );
}

/** « 24 », « 17,5 », « 17.5 » → nombre ; `null` si illisible ou hors bornes. */
function parseHours(text: string): number | null {
  const n = Number(text.trim().replace(',', '.'));
  if (!text.trim() || !Number.isFinite(n) || n <= 0 || n > MAX_HOURS) return null;
  return Math.round(n * 100) / 100;
}
